/**
 * Storyboard boards: a page of aspect-locked frames, each with a notes box
 * (shot number, shot type, camera move, duration, action, dialogue).
 *
 * A board page stores its settings as `storyboard` on the page:
 *   { aspectKey, matteKey, layoutKey, safeAreas }
 * Frames are ordinary cells generated from those settings, so art, bubbles,
 * undo, thumbnails and exports all work unchanged. Each frame also carries
 * `notesBox` (page px) and `shot` (the notes).
 */

/** The shape of the frame itself: what the camera sees. */
export const FRAME_ASPECTS = [
  { key: "16:9", label: "16:9 — HD, YouTube", ratio: 16 / 9 },
  { key: "2.39:1", label: "2.39:1 — Anamorphic scope", ratio: 2.39 },
  { key: "1.85:1", label: "1.85:1 — Theatrical flat", ratio: 1.85 },
  { key: "4:3", label: "4:3 — Academy, classic TV", ratio: 4 / 3 },
  { key: "1:1", label: "1:1 — Square", ratio: 1 },
  { key: "4:5", label: "4:5 — Instagram feed", ratio: 4 / 5 },
  { key: "9:16", label: "9:16 — Reels, TikTok, Shorts", ratio: 9 / 16 },
];

/**
 * A second delivery ratio shown inside the frame. "bars" mattes are solid
 * letterbox/pillarbox bars (the frame shows a wider picture inside a
 * monitor); "guide" mattes shade what a crop for another platform loses.
 */
export const MATTES = [
  { key: "none", label: "No matte" },
  { key: "2.39:1", label: "2.39:1 letterbox bars", ratio: 2.39, mode: "bars" },
  { key: "1.85:1", label: "1.85:1 letterbox bars", ratio: 1.85, mode: "bars" },
  { key: "4:3", label: "4:3 pillarbox bars", ratio: 4 / 3, mode: "bars" },
  { key: "16:9", label: "16:9 crop guide", ratio: 16 / 9, mode: "guide" },
  { key: "4:5", label: "4:5 feed crop guide", ratio: 4 / 5, mode: "guide" },
  { key: "1:1", label: "1:1 square crop guide", ratio: 1, mode: "guide" },
  { key: "9:16", label: "9:16 vertical crop guide", ratio: 9 / 16, mode: "guide" },
];

/** How frames sit on the page and where their notes go. */
export const BOARD_LAYOUTS = [
  { key: "board_3x2", label: "6 frames (3 × 2)", columns: 3, rows: 2, notes: "below" },
  { key: "board_2x2", label: "4 frames (2 × 2)", columns: 2, rows: 2, notes: "below" },
  { key: "board_2x3", label: "6 frames (2 × 3)", columns: 2, rows: 3, notes: "below" },
  { key: "board_3x3", label: "9 thumbnails (3 × 3)", columns: 3, rows: 3, notes: "below", compact: true },
  { key: "board_1x3", label: "3 frames, notes beside", columns: 1, rows: 3, notes: "right" },
  { key: "board_4x1", label: "4 frames in a row", columns: 4, rows: 1, notes: "below" },
  { key: "board_1x1", label: "1 frame (presentation)", columns: 1, rows: 1, notes: "below" },
];

export const SHOT_TYPES = [
  { key: "", label: "Shot…" },
  { key: "EWS", label: "EWS — extreme wide" },
  { key: "WS", label: "WS — wide" },
  { key: "FS", label: "FS — full" },
  { key: "MS", label: "MS — medium" },
  { key: "MCU", label: "MCU — medium close-up" },
  { key: "CU", label: "CU — close-up" },
  { key: "ECU", label: "ECU — extreme close-up" },
  { key: "OTS", label: "OTS — over the shoulder" },
  { key: "POV", label: "POV — point of view" },
  { key: "2S", label: "2S — two shot" },
  { key: "INS", label: "INS — insert" },
];

export const CAMERA_MOVES = [
  { key: "", label: "Move…" },
  { key: "Static", label: "Static" },
  { key: "Pan L", label: "Pan left" },
  { key: "Pan R", label: "Pan right" },
  { key: "Tilt up", label: "Tilt up" },
  { key: "Tilt down", label: "Tilt down" },
  { key: "Push in", label: "Push in" },
  { key: "Pull out", label: "Pull out" },
  { key: "Track", label: "Track / follow" },
  { key: "Truck L", label: "Truck left" },
  { key: "Truck R", label: "Truck right" },
  { key: "Crane up", label: "Crane up" },
  { key: "Crane down", label: "Crane down" },
  { key: "Zoom in", label: "Zoom in" },
  { key: "Zoom out", label: "Zoom out" },
  { key: "Handheld", label: "Handheld" },
  { key: "Whip pan", label: "Whip pan" },
];

export const DEFAULT_STORYBOARD = {
  aspectKey: "16:9",
  matteKey: "none",
  layoutKey: "board_3x2",
  safeAreas: false,
};

// Modern broadcast safe areas (SMPTE ST 2046-1): 93% action, 90% title
export const SAFE_AREAS = [
  { key: "action", label: "Action safe", fraction: 0.93 },
  { key: "title", label: "Title safe", fraction: 0.9 },
];

// Matte and safe-area inks, shared by FrameMatte and the page renderer
export const MATTE_BAR_FILL = "#0d0d0d";
export const MATTE_GUIDE_SHADE = "rgba(13, 13, 13, 0.38)";
export const MATTE_GUIDE_LINE = "rgba(255, 255, 255, 0.9)";
export const SAFE_AREA_LINE = "rgba(26, 26, 26, 0.5)";

export const findFrameAspect = (key) =>
  FRAME_ASPECTS.find((a) => a.key === key) || FRAME_ASPECTS[0];
export const findMatte = (key) => MATTES.find((m) => m.key === key) || MATTES[0];
export const findBoardLayout = (key) =>
  BOARD_LAYOUTS.find((l) => l.key === key) || BOARD_LAYOUTS[0];

/** Fill in any missing settings (pages saved by older builds, partial seeds). */
export const normalizeStoryboard = (storyboard) =>
  storyboard ? { ...DEFAULT_STORYBOARD, ...storyboard } : null;

// ─── Geometry ────────────────────────────────────────────────────────────

const PAGE_MARGIN = 36;
const MIN_SLOT_GAP = 18;
// Space between a frame and its notes
export const NOTES_GAP = 6;
const MIN_NOTES_HEIGHT = 74;
const MIN_COMPACT_NOTES_HEIGHT = 38;
const NOTES_BESIDE_FRACTION = 0.38;

/**
 * Frames and notes boxes for a board page, in reading order (page px).
 * Each frame is the largest rect of the chosen aspect that fits its slot
 * after leaving room for notes; the frame + notes block is centred in it.
 */
export const layoutBoard = ({ pageWidth, pageHeight, gutter = 0, storyboard }) => {
  const { aspectKey, layoutKey } = normalizeStoryboard(storyboard);
  const ratio = findFrameAspect(aspectKey).ratio;
  const layout = findBoardLayout(layoutKey);
  const { columns, rows } = layout;
  const gap = Math.max(MIN_SLOT_GAP, gutter);
  const margin = Math.max(PAGE_MARGIN, gutter);
  const slotW = (pageWidth - margin * 2 - gap * (columns - 1)) / columns;
  const slotH = (pageHeight - margin * 2 - gap * (rows - 1)) / rows;

  const slots = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns; col += 1) {
      const slotX = margin + col * (slotW + gap);
      const slotY = margin + row * (slotH + gap);
      let frame;
      let notes;
      if (layout.notes === "right") {
        const maxFrameW = slotW * (1 - NOTES_BESIDE_FRACTION) - NOTES_GAP;
        const width = Math.min(maxFrameW, slotH * ratio);
        const height = width / ratio;
        const blockY = slotY + (slotH - height) / 2;
        frame = { x: slotX, y: blockY, width, height };
        notes = {
          x: slotX + width + NOTES_GAP * 2,
          y: blockY,
          width: slotW - width - NOTES_GAP * 2,
          height,
        };
      } else {
        const minNotes = layout.compact ? MIN_COMPACT_NOTES_HEIGHT : MIN_NOTES_HEIGHT;
        const width = Math.min(slotW, (slotH - minNotes - NOTES_GAP) * ratio);
        const height = width / ratio;
        // Notes take what's left of the slot, but no more than a frame's worth
        const notesHeight = Math.min(slotH - height - NOTES_GAP, Math.max(minNotes, height));
        const blockH = height + NOTES_GAP + notesHeight;
        const blockX = slotX + (slotW - width) / 2;
        const blockY = slotY + (slotH - blockH) / 2;
        frame = { x: blockX, y: blockY, width, height };
        notes = { x: blockX, y: blockY + height + NOTES_GAP, width, height: notesHeight };
      }
      slots.push({ row, col, frame, notes });
    }
  }
  return slots;
};

/** Fresh frames (cells) for a board page. */
export const createBoardCells = (options) =>
  layoutBoard(options).map(({ row, col, frame, notes }) => ({
    id: `cell-${row}-${col}`,
    key: `R${row + 1}C${col + 1}`,
    ...frame,
    notesBox: notes,
    // No `shot` yet: a rebuild carries the old frame's notes across
    items: [],
  }));

/** True when a frame holds anything worth keeping: art, lettering, or notes. */
export const frameHasContent = (cell) =>
  (cell.items || []).length > 0
  || Object.values(cell.shot || {}).some((v) => v !== "" && v != null);

/**
 * The centred rect of `ratio` inside a w × h frame, or null when the frame
 * already has that shape.
 */
export const matteWindow = (width, height, ratio) => {
  if (!ratio || !(width > 0) || !(height > 0)) return null;
  const frameRatio = width / height;
  if (Math.abs(frameRatio - ratio) < 0.01) return null;
  if (ratio > frameRatio) {
    const h = width / ratio;
    return { x: 0, y: (height - h) / 2, width, height: h };
  }
  const w = height * ratio;
  return { x: (width - w) / 2, y: 0, width: w, height };
};

/** Centred safe-area rects inside a w × h frame. */
export const safeAreaRects = (width, height) =>
  SAFE_AREAS.map((area) => ({
    ...area,
    x: (width * (1 - area.fraction)) / 2,
    y: (height * (1 - area.fraction)) / 2,
    width: width * area.fraction,
    height: height * area.fraction,
  }));

/** "0:08" from seconds. */
export const formatRuntime = (seconds) => {
  const total = Math.max(0, Math.round(seconds || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};
